/**
 * Cedi la squadra (Andrea 2026-10-08): il presidente manda a un'email
 * l'invito a prendere la sua squadra. Quando l'invitato lo usa diventa
 * presidente e chi cede resta nella lega senza squadra, da spettatore.
 * Finche' l'invito e' aperto si puo' annullare.
 */

import { useState } from "react";
import { useAnnullaCessione, useCediSquadra, useCessioneSquadra } from "../../hooks/useFase7";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

function errMsg(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err.response?.data?.detail ?? err.message ?? "errore";
}

export function CediSquadraModal({
  squadraId,
  nomeSquadra,
  onClose,
}: {
  squadraId: string;
  nomeSquadra: string;
  onClose: () => void;
}) {
  const { data: cessione, isLoading } = useCessioneSquadra(squadraId);
  const cediMut = useCediSquadra();
  const annullaMut = useAnnullaCessione();
  const [email, setEmail] = useState("");
  const [conferma, setConferma] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [copiato, setCopiato] = useState(false);

  const onCedi = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrore(null);
    try {
      await cediMut.mutateAsync({ squadraId, email: email.trim() });
      setEmail("");
      setConferma(false);
    } catch (err) {
      setErrore(errMsg(err));
    }
  };

  const onAnnulla = async () => {
    if (!confirm(tr("Ritirare l'invito di cessione? La squadra resta tua."))) return;
    try {
      await annullaMut.mutateAsync({ squadraId });
    } catch (err) {
      setErrore(errMsg(err));
    }
  };

  const copia = async (testo: string) => {
    try {
      await navigator.clipboard.writeText(testo);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 1500);
    } catch {
      /* niente appunti */
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-bg-card border border-accent/40 rounded-card w-full max-w-md max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-primary-soft flex items-center justify-between">
          <div className="font-display text-lg text-accent">💼 Cedi {nomeSquadra}</div>
          <button
            onClick={onClose}
            className="text-fg-muted hover:text-fg text-2xl leading-none"
            aria-label={tr("Chiudi")}
          >
            ×
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          {isLoading ? (
            <div className="text-sm text-fg-muted">{tr("Carico…")}</div>
          ) : cessione ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-warning/50 bg-warning/10 p-3 text-sm text-fg">
                ⏳ Cessione in corso: invito mandato a{" "}
                <b className="text-fg">{cessione.email_destinatario}</b>, valido fino al{" "}
                {new Date(cessione.scadenza).toLocaleDateString(localeCorrente())}. Appena lo
                accetta, la squadra passa a lui e tu resti nella lega come spettatore.
              </div>
              <div className="text-xs text-fg-muted">
                {tr("Se l'email non arriva, passagli il link o il codice:")}
              </div>
              <div className="flex items-center gap-2 text-xs">
                <code className="font-mono text-accent bg-primary-deep/60 border border-accent/30 rounded px-2 py-1">
                  {cessione.codice_leggibile}
                </code>
                <button
                  type="button"
                  onClick={() => void copia(cessione.link_invito)}
                  className="btn-ghost text-xs"
                >
                  {copiato ? tr("✓ Copiato") : tr("Copia link")}
                </button>
              </div>
              <button
                type="button"
                onClick={() => void onAnnulla()}
                disabled={annullaMut.isPending}
                className="btn-ghost border border-danger/40 text-danger hover:bg-danger/10 w-full text-sm disabled:opacity-50"
              >
                {tr("Annulla la cessione")}
              </button>
            </div>
          ) : (
            <form onSubmit={onCedi} className="space-y-3">
              <p className="text-sm text-fg-muted">
                {tr("Chi riceve l'invito prende")} <b className="text-fg">{nomeSquadra}</b> com'è:
                rosa, budget, risultati. Tu resti nella lega senza squadra, da spettatore; potrai
                tornare in una squadra solo con un invito da allenatore in seconda.
              </p>
              <div>
                <label
                  className="block text-xs uppercase tracking-wider text-fg-muted mb-1"
                  htmlFor="cessione-email"
                >
                  {tr("Email del nuovo presidente")}
                </label>
                <input
                  id="cessione-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@esempio.it"
                  className="w-full rounded-lg bg-bg border border-primary-soft/50 px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:border-accent"
                />
                <div className="text-[11px] text-fg-muted mt-1">
                  {tr(
                    tr(
                      "Va bene anche chi ha già un account FantaSquad: accetta dal link da loggato.",
                    ),
                  )}
                </div>
              </div>
              <label className="flex items-start gap-2 text-xs text-fg">
                <input
                  type="checkbox"
                  checked={conferma}
                  onChange={(e) => setConferma(e.target.checked)}
                  className="mt-0.5"
                />
                {tr("Ho capito: quando l'invito viene accettato la squadra non è più mia.")}
              </label>
              {errore && <div className="text-xs text-danger">{errore}</div>}
              <button
                type="submit"
                disabled={!conferma || !email.trim() || cediMut.isPending}
                className="btn-primary w-full disabled:opacity-50"
              >
                {cediMut.isPending ? tr("Mando l'invito…") : tr("Manda l'invito di cessione")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
